import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import EditedPageRaster from './EditedPageRaster';
import { alignEditorInkToField } from './pdfTextAlignment';
import FontPicker from './FontPicker';
import {
  exactPdfFontPresentation,
  ensureExactPdfFont,
  exactMatriceBitsFontReady,
  useEmbeddedPdfFonts,
} from './pdfFontCompat';
import './DirectEditOverrides.css';


function number(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function resolveEditValue(originalValue, localExists, localValue, parentExists, parentValue) {
  const original = String(originalValue ?? '');
  if (localExists) {
    const value = String(localValue ?? '');
    return { value, hasEdit: value !== original };
  }
  if (parentExists) {
    const value = String(parentValue ?? '');
    return { value, hasEdit: value !== original };
  }
  return { value: original, hasEdit: false };
}

function getCharacterBox(char) {
  const p = char?.position || {};
  const size = char?.size || {};
  const x = number(p.bbox_x ?? p.x, 0);
  const y = number(p.bbox_y, 0);
  const width = Math.max(number(p.bbox_right, x + number(size.width, char?.font?.size || 12)) - x, 0.01);
  const height = Math.max(number(p.bbox_bottom, y + number(size.height, char?.font?.size || 12)) - y, 0.01);
  return { x, y, width, height };
}

function sameFont(a, b) {
  if (!a || !b) return true;
  const ax = Number(a.xref);
  const bx = Number(b.xref);
  if (Number.isFinite(ax) && Number.isFinite(bx)) return ax === bx;
  const ac = String(a.css_family || '').trim();
  const bc = String(b.css_family || '').trim();
  if (ac && bc) return ac === bc;
  const an = String(a.basefont || a.name || '').replace(/^[A-Z]{6}\+/, '').toLowerCase();
  const bn = String(b.basefont || b.name || '').replace(/^[A-Z]{6}\+/, '').toLowerCase();
  return !an || !bn || an === bn;
}

function charsForField(page, field) {
  const pageChars = Array.isArray(page?.characters) ? page.characters : [];
  const refs = [
    ...(field?.source?.character_global_indexes || []),
    ...(field?.source_character_indexes || []),
    ...(field?.character_indexes || []),
  ].map(Number).filter((value) => Number.isInteger(value) && value >= 0 && value < pageChars.length);
  if (refs.length) {
    return Array.from(new Set(refs)).map((indexValue, index) => ({
      ...pageChars[indexValue], __index: index, __box: getCharacterBox(pageChars[indexValue]),
    }));
  }
  const sourceChars = Array.isArray(field?.source_characters) ? field.source_characters : Array.isArray(field?.characters) ? field.characters : [];
  if (sourceChars.length) {
    return sourceChars.map((sourceChar, index) => {
      const globalIndex = Number(sourceChar?.global_char_index);
      const resolved = Number.isInteger(globalIndex) && pageChars[globalIndex] ? pageChars[globalIndex] : sourceChar;
      return { ...(resolved || sourceChar), __index: index, __box: getCharacterBox(resolved || sourceChar) };
    });
  }
  const bbox = field?.bbox || {};
  const x0 = number(bbox.x0, 0);
  const y0 = number(bbox.y0, 0);
  const x1 = number(bbox.x1, x0 + number(bbox.width, 0));
  const y1 = number(bbox.y1, y0 + number(bbox.height, 0));
  if (x1 <= x0 || y1 <= y0) return [];
  return pageChars.map((char, index) => ({ ...char, __index: index, __box: getCharacterBox(char) })).filter((char) => {
    const box = char.__box;
    return Math.min(box.x + box.width, x1) > Math.max(box.x, x0) && Math.min(box.y + box.height, y1) > Math.max(box.y, y0);
  });
}

/* Legacy JSON fallback: keep span boundaries, then split only on a genuinely large geometric gap or font change. */
function splitLegacySpan(chars, prefix) {
  const sorted = [...chars].sort((a, b) => a.__box.x - b.__box.x || Number(a.__index) - Number(b.__index));
  const parts = [];
  let current = [];
  const flush = () => {
    if (!current.length) return;
    const visible = current.filter((char) => String(char?.text ?? '').trim());
    if (!visible.length) { current = []; return; }
    const boxes = current.map((char) => char.__box);
    const left = Math.min(...boxes.map((box) => box.x));
    const top = Math.min(...boxes.map((box) => box.y));
    const right = Math.max(...boxes.map((box) => box.x + box.width));
    const bottom = Math.max(...boxes.map((box) => box.y + box.height));
    const first = visible[0];
    parts.push({
      id: `${prefix}:part:${parts.length}`,
      chars: [...current],
      text: current.map((char) => String(char?.text ?? '')).join('').trim(),
      x: left, top,
      width: Math.max(1, right - left),
      height: Math.max(1, bottom - top),
      font: first?.font || {}, font_lock: null,
      color: first?.color || '#000000',
    });
    current = [];
  };

  for (let i = 0; i < sorted.length; i += 1) {
    const char = sorted[i];
    const prev = sorted[i - 1];
    const fontSize = Number(char?.font?.size || prev?.font?.size || 12);
    if (current.length && prev) {
      const gap = char.__box.x - (prev.__box.x + prev.__box.width);
      const previousWasSpace = !String(prev?.text ?? '').trim();
      const currentIsSpace = !String(char?.text ?? '').trim();
      if (!sameFont(prev?.font, char?.font) || (gap > Math.max(6, fontSize * 1.15) && !previousWasSpace && !currentIsSpace)) {
        flush();
      }
    }
    current.push(char);

    if (String(char?.text ?? '').trim()) {
      let whitespaceWidth = 0;
      let j = i + 1;
      while (j < sorted.length && !String(sorted[j]?.text ?? '').trim()) {
        whitespaceWidth += sorted[j].__box.width;
        j += 1;
      }
      if (j < sorted.length && whitespaceWidth > Math.max(6, fontSize * 1.1)) flush();
    }
  }
  flush();
  return parts.filter((part) => part.text.length > 0);
}

function groupCharacters(characters) {
  const spanMap = new Map();
  (characters || []).forEach((char, index) => {
    const key = `${char?.block ?? 0}:${char?.line ?? 0}:${char?.span ?? index}`;
    if (!spanMap.has(key)) spanMap.set(key, []);
    spanMap.get(key).push({ ...char, __index: index, __box: getCharacterBox(char) });
  });
  const fields = [];
  for (const [key, chars] of spanMap.entries()) fields.push(...splitLegacySpan(chars, `legacy_${key}`));
  return fields;
}

function buildEditableFields(page) {
  const explicitFields = Array.isArray(page?.fields) ? page.fields : [];
  if (explicitFields.length) {
    return explicitFields.map((field, index) => {
      const bbox = field?.bbox || {};
      const chars = charsForField(page, field);
      const first = chars.find((char) => String(char?.text || '').trim());
      const font = Object.keys(field?.font || {}).length ? field.font : (first?.font || {});
      const lock = Object.keys(field?.font_lock || {}).length ? field.font_lock : font;
      return {
        id: String(field?.id || `field_${index}`),
        text: String(field?.text ?? chars.map((char) => char?.text ?? '').join('')).trim(),
        x: number(bbox.x0 ?? field?.position?.x, 0), top: number(bbox.y0, 0),
        width: Math.max(1, number(bbox.width, number(bbox.x1) - number(bbox.x0))),
        height: Math.max(1, number(bbox.height, number(bbox.y1) - number(bbox.y0))),
        font, font_lock: lock, color: field?.color || first?.color || '#000000', chars, rawField: field,
      };
    });
  }
  return groupCharacters(page?.characters);
}

function measureTextWidth(text, presentation, fontSizePx) {
  const canvas = measureTextWidth.canvas || (measureTextWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  if (!context) return String(text || '').length * fontSizePx * 0.55;
  context.font = `${presentation.style} ${presentation.weight} ${fontSizePx}px ${presentation.family}`;
  if ('fontKerning' in context) context.fontKerning = 'normal';
  return Math.max(0, context.measureText(String(text || '')).width);
}

function textOffsetFromSelection(element) {
  const selection = element.ownerDocument?.getSelection?.();
  if (!selection || !selection.rangeCount || !element.contains(selection.anchorNode)) return null;
  const range = selection.getRangeAt(0).cloneRange();
  const before = range.cloneRange();
  before.selectNodeContents(element);
  before.setEnd(selection.anchorNode, selection.anchorOffset);
  return before.toString().length;
}

function restoreTextOffset(element, offset) {
  if (!element || !Number.isFinite(offset)) return false;
  const doc = element.ownerDocument;
  const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let remaining = Math.max(0, offset);
  let node = walker.nextNode();
  while (node) {
    const length = node.nodeValue?.length || 0;
    if (remaining <= length) {
      const range = doc.createRange();
      range.setStart(node, remaining); range.collapse(true);
      const selection = doc.getSelection(); selection.removeAllRanges(); selection.addRange(range);
      return true;
    }
    remaining -= length; node = walker.nextNode();
  }
  const range = doc.createRange(); range.selectNodeContents(element); range.collapse(false);
  const selection = doc.getSelection(); selection.removeAllRanges(); selection.addRange(range);
  return true;
}

function placeCaretAtPoint(element, clientX, clientY) {
  if (!element) return false;
  try {
    const doc = element.ownerDocument;
    let range = null;
    if (typeof doc.caretRangeFromPoint === 'function') range = doc.caretRangeFromPoint(clientX, clientY);
    else if (typeof doc.caretPositionFromPoint === 'function') {
      const position = doc.caretPositionFromPoint(clientX, clientY);
      if (position) { range = doc.createRange(); range.setStart(position.offsetNode, position.offset); range.collapse(true); }
    }
    if (!range || !element.contains(range.startContainer)) return false;
    const selection = doc.getSelection(); selection.removeAllRanges(); selection.addRange(range); return true;
  } catch { return false; }
}

function renderReceiptDrawings(drawings, W, H) {
  if (!Array.isArray(drawings) || drawings.length === 0) return null;
  return <svg className="receipt-vector-drawings" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
    {drawings.flatMap((drawing) => (drawing.items || []).map((item, index) => {
      const common = { key: `receipt-drawing-${drawing.id}-${index}`, stroke: drawing.stroke || 'none', fill: drawing.fill || 'none', strokeWidth: Number(drawing.width || 0), strokeOpacity: drawing.stroke_opacity ?? undefined, fillOpacity: drawing.fill_opacity ?? undefined, vectorEffect: 'non-scaling-stroke' };
      if (item.type === 'line') return <line {...common} x1={item.x1} y1={item.y1} x2={item.x2} y2={item.y2} />;
      if (item.type === 'rect') return <rect {...common} x={item.x} y={item.y} width={item.width} height={item.height} />;
      if (item.type === 'quad') return <polygon {...common} points={(item.points || []).map((point) => `${point.x},${point.y}`).join(' ')} />;
      if (item.type === 'curve' && (item.points || []).length === 4) { const p = item.points; return <path {...common} d={`M ${p[0].x} ${p[0].y} C ${p[1].x} ${p[1].y}, ${p[2].x} ${p[2].y}, ${p[3].x} ${p[3].y}`} />; }
      return null;
    }))}
  </svg>;
}

export default function ReceiptPreview({ template, formData = {}, onChange }) {
  const fontRevision = useEmbeddedPdfFonts(template);
  const [edits, setEdits] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [selectedFonts, setSelectedFonts] = useState({});
  const [zoom, setZoom] = useState(1);
  const fieldRefs = useRef(new Map());
  const pageRefs = useRef(new Map());
  const pendingCaret = useRef(null);
  const page = template?.pages?.[0];
  const fields = useMemo(() => buildEditableFields(page), [page]);

  useEffect(() => { setEdits({}); setEditingId(null); setSelectedFonts({}); pendingCaret.current = null; }, [template]);

  const W = number(page?.dimensions?.width, 595);
  const H = number(page?.dimensions?.height, 842);
  const baseScale = Math.min(1, 620 / W);
  const scale = baseScale * zoom;
  const rasterSrc = page?.rendered_page?.base64 ? `data:image/${String(page.rendered_page?.format || 'png').replace('jpg', 'jpeg')};base64,${page.rendered_page.base64}` : '';
  const hasRaster = Boolean(rasterSrc);

  useLayoutEffect(() => {
    for (const field of fields) {
      const el = fieldRefs.current.get(field.id);
      if (!el) continue;
      const hasLocalEdit = Object.prototype.hasOwnProperty.call(edits, field.id);
      const hasParentEdit = Object.prototype.hasOwnProperty.call(formData, `text_${field.id}`);
      const resolved = resolveEditValue(field.text, hasLocalEdit, edits[field.id], hasParentEdit, formData[`text_${field.id}`]);
      if (editingId !== field.id && el.textContent !== (resolved.hasEdit ? resolved.value : '')) {
        el.textContent = resolved.hasEdit ? resolved.value : '';
      }
    }

    if (!editingId) return;
    const field = fields.find((item) => item.id === editingId);
    const el = fieldRefs.current.get(editingId);
    if (!field || !el) return;
    const key = `text_${field.id}`;
    const hasLocalEdit = Object.prototype.hasOwnProperty.call(edits, field.id);
    const hasParentEdit = Object.prototype.hasOwnProperty.call(formData, key);
    const resolved = resolveEditValue(field.text, hasLocalEdit, edits[field.id], hasParentEdit, formData[key]);
    if (el.textContent !== resolved.value) el.textContent = resolved.value;
    el.focus({ preventScroll: true });
    const pending = pendingCaret.current;
    if (pending?.fieldId === field.id) {
      let placed = false;
      if (Number.isFinite(pending.offset)) placed = restoreTextOffset(el, pending.offset);
      else if (Number.isFinite(pending.clientX) && Number.isFinite(pending.clientY)) placed = placeCaretAtPoint(el, pending.clientX, pending.clientY);
      if (!placed) restoreTextOffset(el, el.textContent?.length || 0);
    } else if (document.activeElement !== el) {
      restoreTextOffset(el, el.textContent?.length || 0);
    }
  }, [editingId, fields, edits, formData]);

  useLayoutEffect(() => {
    const pageElement = pageRefs.current.get('receipt');
    if (!pageElement) return;
    for (const field of fields) {
      const key = field.id;
      const el = fieldRefs.current.get(key);
      if (!el) continue;
      const local = Object.prototype.hasOwnProperty.call(edits, field.id);
      const parent = Object.prototype.hasOwnProperty.call(formData, `text_${field.id}`);
      const resolved = resolveEditValue(field.text, local, edits[field.id], parent, formData[`text_${field.id}`]);
      if (!resolved.value) continue;
      alignEditorInkToField({ element: el, pageElement, field, scale });
    }
  }, [fields, edits, formData, zoom, scale, editingId]);

  if (!page) return <div className="empty-preview">Aucun template chargé</div>;

  const maskBoxes = fields.flatMap((field) => {
    const key = `text_${field.id}`;
    const local = Object.prototype.hasOwnProperty.call(edits, field.id);
    const parent = Object.prototype.hasOwnProperty.call(formData, key);
    const resolved = resolveEditValue(field.text, local, edits[field.id], parent, formData[key]);
    if (!resolved.hasEdit) return [];
    return field.chars.length
      ? field.chars.filter((char) => String(char?.text ?? '').trim()).map((char) => ({ ...char.__box || getCharacterBox(char) }))
      : [{ x: field.x, y: field.top, width: field.width, height: field.height }];
  });

  const changeZoom = (value) => setZoom(Math.min(5, Math.max(0.5, Number(value.toFixed(2)))));

  return (
    <div className="receipt-preview-wrapper" data-font-revision={fontRevision}>
      <div className="zoom-toolbar" role="toolbar" aria-label="Zoom de l’aperçu">
        <div className="zoom-controls">
          <button type="button" className="zoom-control-button" onClick={() => changeZoom(zoom - 0.1)} aria-label="Dézoomer">−</button>
          <button type="button" className="zoom-percentage" onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
          <button type="button" className="zoom-control-button" onClick={() => changeZoom(zoom + 0.1)} aria-label="Zoomer">+</button>
          <button type="button" className="zoom-reset-button" onClick={() => setZoom(1)}>Réinitialiser</button>
        </div>
        <div className="zoom-slider-container">
          <span className="zoom-slider-label">50%</span>
          <input type="range" min="50" max="500" step="5" value={Math.round(zoom * 100)} onChange={(event) => setZoom(Number(event.target.value) / 100)} className="zoom-slider" aria-label="Niveau de zoom" />
          <span className="zoom-slider-label">500%</span>
        </div>
      </div>

      <div className="receipt-scroll-area">
        <div className="receipt-document-space" style={{ width: W * scale, height: H * scale }}>
          <div ref={(el) => { if (el) pageRefs.current.set('receipt', el); else pageRefs.current.delete('receipt'); }} className="receipt-canvas" data-pdf-page-width={W} data-pdf-page-height={H} style={{ width: W * scale, height: H * scale }}>
            {hasRaster ? <EditedPageRaster src={rasterSrc} pageWidth={W} pageHeight={H} maskBoxes={maskBoxes} className="receipt-pdf-raster" style={{ position: 'absolute', left: 0, top: 0, width: W * scale, height: H * scale, zIndex: 0, pointerEvents: 'none', userSelect: 'none' }} /> : renderReceiptDrawings(page.drawings, W, H)}

            {fields.map((field) => {
              const key = `text_${field.id}`;
              const local = Object.prototype.hasOwnProperty.call(edits, field.id);
              const parent = Object.prototype.hasOwnProperty.call(formData, key);
              const resolved = resolveEditValue(field.text, local, edits[field.id], parent, formData[key]);
              const hasEdit = resolved.hasEdit;
              const isEditing = editingId === field.id;
              const selectedFont = selectedFonts[field.id] || null;
              const presentation = exactPdfFontPresentation(template, field, selectedFont);
              const fontSize = Math.max(1, presentation.sizePt * scale);
              const measuredWidth = Math.max(0, measureTextWidth(resolved.value, presentation, fontSize));
              const fieldWidth = hasEdit ? Math.max(field.width * scale, Math.ceil(measuredWidth + Math.max(4, scale * 3))) : Math.max(1, field.width * scale);
              const fieldHeight = Math.max(1, field.height * scale);
              return (
                <React.Fragment key={field.id}>
                  {isEditing && (
                    <FontPicker
                      selectedFont={selectedFont}
                      onSelect={(font) => {
                        setSelectedFonts((previous) => ({ ...previous, [field.id]: font }));
                        if (font?.id === 'matricebits-z2') void exactMatriceBitsFontReady();
                        const currentElement = fieldRefs.current.get(field.id);
                        if (currentElement) {
                          const offset = textOffsetFromSelection(currentElement);
                          pendingCaret.current = { ...(pendingCaret.current || {}), fieldId: field.id, offset, clientX: null, clientY: null };
                        }
                      }}
                      style={{ left: field.x * scale, top: field.top * scale - 30 }}
                    />
                  )}
                  <div
                    key={`editor-${field.id}`}
                  ref={(el) => { if (el) fieldRefs.current.set(field.id, el); else fieldRefs.current.delete(field.id); }}
                  className={`direct-editable-text ${isEditing ? 'is-editing' : ''} ${hasEdit ? 'has-edit' : ''}`}
                  data-pdf-editor-field="true"
                  data-direct-text-edit="true"
                  contentEditable={isEditing}
                  suppressContentEditableWarning
                  spellCheck={false}
                  onDoubleClick={async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    pendingCaret.current = { fieldId: field.id, clientX: event.clientX, clientY: event.clientY, offset: null };
                    // Never expose the replacement text before the field font is ready.
                    await ensureExactPdfFont(template, field, selectedFonts[field.id] || null);
                    if (fieldRefs.current.get(field.id)) setEditingId(field.id);
                  }}
                  onInput={(event) => {
                    const value = event.currentTarget.textContent || '';
                    const offset = textOffsetFromSelection(event.currentTarget);
                    pendingCaret.current = { ...(pendingCaret.current || {}), fieldId: field.id, offset, clientX: null, clientY: null };
                    setEdits((previous) => ({ ...previous, [field.id]: value }));
                    onChange?.({ ...formData, [key]: value });
                  }}
                  onBlur={() => { if (editingId === field.id) { setEditingId(null); pendingCaret.current = null; } }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
                    if (event.key === 'Escape') { event.preventDefault(); const el = event.currentTarget; el.textContent = hasEdit ? resolved.value : field.text; el.blur(); }
                  }}
                  onPaste={(event) => {
                    event.preventDefault();
                    const plain = event.clipboardData?.getData('text/plain') || '';
                    const selection = event.currentTarget.ownerDocument.getSelection();
                    if (!selection || !selection.rangeCount) return;
                    selection.deleteFromDocument();
                    const range = selection.getRangeAt(0);
                    range.insertNode(event.currentTarget.ownerDocument.createTextNode(plain));
                    range.collapse(false);
                    selection.removeAllRanges(); selection.addRange(range);
                    event.currentTarget.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: plain }));
                  }}
                  style={{
                    position: 'absolute', left: field.x * scale, top: field.top * scale,
                    width: fieldWidth, minWidth: field.width * scale, height: fieldHeight, minHeight: field.height * scale,
                    overflow: 'visible', background: 'transparent', outline: 'none', border: 0,
                    /* IMPORTANT: entering edit mode keeps the original raster visible until the value really changes. */
                    color: hasEdit ? field.color : 'transparent', WebkitTextFillColor: hasEdit ? field.color : 'transparent',
                    fontFamily: presentation.family, fontSize, fontWeight: presentation.weight, fontStyle: presentation.style,
                    lineHeight: `${fontSize}px`, letterSpacing: 0, fontKerning: 'normal', fontVariantLigatures: 'normal', fontSynthesis: 'none',
                    textRendering: 'geometricPrecision', whiteSpace: 'pre', direction: 'ltr', unicodeBidi: 'plaintext',
                    padding: 0, margin: 0, boxSizing: 'border-box', transform: 'none', transformOrigin: 'left top',
                    caretColor: isEditing ? '#111827' : 'transparent', cursor: 'text', userSelect: isEditing ? 'text' : 'none', WebkitUserSelect: isEditing ? 'text' : 'none',
                    pointerEvents: isEditing || !hasEdit ? 'auto' : 'auto', zIndex: hasEdit || isEditing ? 20 : 5,
                  }}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
